import styled from 'styled-components'
import { Form } from 'react-bootstrap'

export const FORM_FONT = `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`

const MOBILE_BREAKPOINT = '767px'

export const CenteredFormTitle = styled.h1`
  font-family: ${FORM_FONT};
  font-weight: 700;
  font-size: 24px;
  line-height: 120%;
  text-align: center;
  color: #464646;
  padding-top: 3rem;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 20px;
    padding-top: 30px;
    line-height: 1.25;
  }
`

export const FocalPointTitle = styled.h3`
  font-family: ${FORM_FONT};
  font-weight: 700;
  font-size: 1.3rem;
  color: #464646;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
  padding-left: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 13px;
    margin-bottom: 0.8rem;
    margin-top: 0.3rem;
  }
`

export const AddFocalPointButton = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  font-family: ${FORM_FONT};
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 0.75rem;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 13px;
    margin-top: 0.35rem;
    gap: 3px;
  }
`

export const FocalPointDivider = styled.hr`
  margin: 1.25rem 0 0.25rem 0;
  border-color: #ddd;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0.5rem 0 0.15rem 0;
  }
`

export const FocalPointCard = styled.div`
  position: relative;
`

export const RemoveFocalPointButton = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;

  &:hover {
    color: #dc3545;
  }
`

export const FieldLabel = styled.label`
  color: var(--text-secondary);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  margin-bottom: 4px;
  display: block;

  span.required {
    color: #dc3545;
    margin-left: 2px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 2px;
    line-height: 1.3;
  }
`

export const LogoPreviewError = styled.div`
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 4px;
`

export const StyledSelect = styled(Form.Select)`
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  appearance: none;
  border-radius: 0.375rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  -webkit-tap-highlight-color: transparent;

  option {
    font-size: 1rem;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.25rem 0.4rem;
    min-height: 34px;
    font-size: 14px;
    border-radius: 0.25rem;
    line-height: 1.35;

    option {
      font-size: 14px;
    }
  }
`

export const StyledTextarea = styled(Form.Control).attrs({ as: 'textarea' })`
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  -webkit-tap-highlight-color: transparent;

  &::placeholder {
    font-size: 1rem;
    font-weight: 400;
    color: #6c757d;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.25rem 0.4rem;
    min-height: 52px;
    font-size: 14px;
    border-radius: 0.25rem;
    line-height: 1.35;

    &::placeholder {
      font-size: 14px;
    }
  }
`

export const FormWrapper = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 0.5rem;
    max-width: 100%;
    box-sizing: border-box;

    .row {
      margin-left: -0.25rem;
      margin-right: -0.25rem;
    }

    .row.my-2 {
      margin-top: 0.35rem;
      margin-bottom: 0.35rem;
    }

    .row > [class*="col"] {
      padding-left: 0.25rem;
      padding-right: 0.25rem;
    }

    input, select, textarea, button, a {
      -webkit-tap-highlight-color: transparent;
    }

    input, select, textarea {
      font-size: 14px;
    }

    input:focus, select:focus, textarea:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(0, 102, 179, 0.25);
    }

    .form-control, .form-select {
      padding: 0.25rem 0.4rem;
      min-height: 34px;
      font-size: 14px;
      line-height: 1.35;
    }

    .form-control::placeholder {
      font-size: 14px;
    }

    .invalid-feedback {
      font-size: 11px;
    }

    hr {
      margin: 0.5rem 0;
    }

    [class*="SectionTitle"] {
      font-size: 14px;
      padding: 4px 2px;
      margin-bottom: 0.4rem;
      border-bottom-width: 2px;
    }

    .row label {
      font-size: 14px;
    }

    [class*="SectionTitle"] span {
      font-size: 14px;
    }

    p, span, small, .text-muted, .character-count {
      font-size: 13px;
    }

    a {
      font-size: 13px;
    }
  }
`

export const StepButtonsContainer = styled.div<{ $isLastStep: boolean }>`
  display: flex;
  justify-content: ${({ $isLastStep }) => ($isLastStep ? 'center' : 'flex-end')};
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.2rem 0;
    margin-top: 0.35rem;
    margin-bottom: 0.35rem;
  }
`

export const CheckboxGroup = styled.div`
  margin-top: 2px;

  .form-check {
    padding-left: 1.5em;
    margin-bottom: 4px;
  }

  .form-check-label {
    font-size: 16px;
    font-weight: 400;
    line-height: 140%;
  }

  .form-check-input {
    margin-top: 0.2em;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-top: 0;

    .form-check-label {
      font-size: 13px;
      line-height: 1.3;
    }

    .form-check {
      padding-left: 1.25em;
      margin-bottom: 2px;
    }

    .form-check-input {
      width: 0.9em;
      height: 0.9em;
    }
  }
`

export const ProximoButton = styled.button`
  width: auto;
  height: 40px;
  padding: 0 24px;
  border-radius: 4px;
  border: none;
  background-color: #c9e4f9;
  color: #0066b3;
  font-family: ${FORM_FONT};
  font-weight: 700;
  font-size: 16px;
  line-height: 140%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    height: 36px;
    font-size: 13px;
    padding: 0 10px;
    border-radius: 0.25rem;
  }
`

export const CadastrarButton = styled.button`
  width: 100%;
  height: 48px;
  border-radius: 4px;
  border: none;
  background-color: #0066b3;
  color: #ffffff;
  font-family: ${FORM_FONT};
  font-weight: 700;
  font-size: 16px;
  line-height: 140%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: #00477d;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 36px;
    font-size: 13px;
    border-radius: 0.25rem;
  }
`

export const AddLogoButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 20px;
  border-radius: 4px;
  background: #a0d6ff;
  border: 1px solid #a0d6ff;
  color: #01579e;
  font-size: 14px;
  font-weight: 700;
  line-height: 140%;
  cursor: pointer;
  width: fit-content;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  box-shadow: 0 2px 0 rgba(0,0,0,0.02);
  -webkit-tap-highlight-color: transparent;

  input {
    display: none;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 32px;
    padding: 0 10px;
    font-size: 13px;
    gap: 5px;
    border-radius: 0.25rem;
  }
`

export const RemoveLogoButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #0d6efd;
  border: 2px solid white;
  color: white;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  -webkit-tap-highlight-color: transparent;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 22px;
    height: 22px;
    font-size: 10px;
    top: -2px;
    right: -2px;
    border-width: 1px;
  }
`

export const LogoPreviewImage = styled.img`
  width: 240px;
  height: 74px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e5e7eb;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 140px;
    height: 44px;
    border-radius: 0.25rem;
  }
`
